import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import api from '../utils/api';
import { toast } from 'sonner';
import { Clock, Plus, Calendar, ChevronLeft, ChevronRight, Trash, Edit, Play, Check, Zap, Target, MessageSquare, Phone, BookOpen, FileText, Users, Coffee, Bell, BellOff } from 'lucide-react';

const activityTypes = [
  { value: 'Prospecting', label: 'Prospecting', icon: Target, color: '#E31837' },
  { value: 'Hunt Messages', label: 'Hunt Messages', icon: Zap, color: '#DC2626' },
  { value: 'Farm Messages', label: 'Farm Messages', icon: MessageSquare, color: '#F59E0B' },
  { value: 'Cultivate Messages', label: 'Cultivate Messages', icon: Users, color: '#8B5CF6' },
  { value: 'Appointments/Calls', label: 'Appointments/Calls', icon: Phone, color: '#3B82F6' },
  { value: 'Follow-ups', label: 'Follow-ups', icon: Check, color: '#10B981' },
  { value: 'Training/Learning', label: 'Training/Learning', icon: BookOpen, color: '#6366F1' },
  { value: 'Admin/Paperwork', label: 'Admin/Paperwork', icon: FileText, color: '#78716C' },
  { value: 'Presentations', label: 'Presentations', icon: Play, color: '#EC4899' },
  { value: 'Team Meeting', label: 'Team Meeting', icon: Users, color: '#0EA5E9' },
  { value: 'Personal/Break', label: 'Personal/Break', icon: Coffee, color: '#6B7280' },
  { value: 'Custom', label: 'Custom', icon: Calendar, color: '#A855F7' }
];

const recurrenceOptions = [
  { value: 'none', label: 'One-time' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays (Mon-Fri)' },
  { value: 'weekly', label: 'Weekly (Select Days)' }
];

const daysOfWeek = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' }
];

const hours = Array.from({ length: 17 }, (_, i) => i + 6); // 6 AM to 10 PM

export const TimeBlockingPage = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [timeBlocks, setTimeBlocks] = useState([]);
  const [weekSchedule, setWeekSchedule] = useState({});
  const [templates, setTemplates] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [activeView, setActiveView] = useState('week');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const notifiedBlocksRef = useRef(new Set());
  
  const [formData, setFormData] = useState({
    title: '',
    activity_type: 'Prospecting',
    start_time: '09:00',
    end_time: '10:00',
    date: '',
    recurrence: 'none',
    days_of_week: [],
    color: '#E31837',
    notes: '',
    reminder_minutes: 5
  });

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'granted') {
        setNotificationsEnabled(true);
      }
    }
  }, []);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('This browser does not support notifications');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Notifications enabled! You will receive reminders before your scheduled activities.');
        
        // Show test notification
        new Notification('VRMX', {
          body: 'Notifications are now enabled! You will receive reminders before your scheduled time blocks.',
          icon: '/favicon.ico',
          tag: 'test-notification'
        });
      } else {
        toast.error('Notification permission denied');
      }
    } catch (error) {
      toast.error('Failed to request notification permission');
    }
  };

  // Toggle notifications
  const toggleNotifications = () => {
    if (notificationsEnabled) {
      setNotificationsEnabled(false);
      toast.info('Notifications disabled');
    } else {
      if (notificationPermission === 'granted') {
        setNotificationsEnabled(true);
        toast.success('Notifications enabled');
      } else {
        requestNotificationPermission();
      }
    }
  };

  // Send notification for upcoming time block
  const sendNotification = useCallback((block, minutesUntilStart) => {
    if (!notificationsEnabled || notificationPermission !== 'granted') return;
    
    const notificationKey = `${block.id}-${new Date().toDateString()}`;
    if (notifiedBlocksRef.current.has(notificationKey)) return;
    
    notifiedBlocksRef.current.add(notificationKey);
    
    const title = minutesUntilStart <= 1 
      ? `Starting Now: ${block.title}` 
      : `Starting in ${minutesUntilStart} minutes: ${block.title}`;
    
    const notification = new Notification('VRMX', {
      body: `${title}\n${block.activity_type} - ${block.start_time} to ${block.end_time}`,
      icon: '/favicon.ico',
      tag: `block-${block.id}`,
      requireInteraction: true
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };

    // Also show toast in app
    toast.info(`⏰ ${title}`, {
      description: `${block.activity_type} - ${block.start_time} to ${block.end_time}`,
      duration: 10000
    });
  }, [notificationsEnabled, notificationPermission]);

  // Check for upcoming time blocks every minute
  useEffect(() => {
    if (!notificationsEnabled) return;

    const checkUpcomingBlocks = () => {
      const now = new Date();
      const todayStr = now.toISOString().split('T')[0];
      const todayBlocks = weekSchedule[todayStr] || [];
      
      todayBlocks.forEach(block => {
        const [blockHour, blockMin] = block.start_time.split(':').map(Number);
        const blockTime = new Date(now);
        blockTime.setHours(blockHour, blockMin, 0, 0);
        
        const minutesUntilStart = Math.round((blockTime - now) / 60000);
        const reminderMinutes = block.reminder_minutes || 5;
        
        // Send notification if within reminder window
        if (minutesUntilStart > 0 && minutesUntilStart <= reminderMinutes) {
          sendNotification(block, minutesUntilStart);
        }
        
        // Send "starting now" notification
        if (minutesUntilStart >= -1 && minutesUntilStart <= 1) {
          sendNotification(block, 0);
        }
      });
    };

    // Check immediately and then every 30 seconds
    checkUpcomingBlocks();
    const interval = setInterval(checkUpcomingBlocks, 30000);
    
    return () => clearInterval(interval);
  }, [notificationsEnabled, weekSchedule, sendNotification]);

  // Clear notified blocks at midnight
  useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    
    const msUntilMidnight = tomorrow - now;
    
    const timeout = setTimeout(() => {
      notifiedBlocksRef.current.clear();
    }, msUntilMidnight);
    
    return () => clearTimeout(timeout);
  }, []);

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  }

  const fetchTimeBlocks = useCallback(async () => {
    try {
      const response = await api.get('/time-blocks');
      setTimeBlocks(response.data);
    } catch (error) {
      toast.error('Failed to load time blocks');
    }
  }, []);

  const fetchWeekSchedule = useCallback(async () => {
    try {
      const startStr = weekStart.toISOString().split('T')[0];
      const response = await api.get(`/time-blocks/week/${startStr}`);
      setWeekSchedule(response.data);
    } catch (error) {
      toast.error('Failed to load week schedule');
    }
  }, [weekStart]);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await api.get('/time-blocks/templates');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to load templates');
    }
  }, []);

  useEffect(() => {
    fetchTimeBlocks();
    fetchWeekSchedule();
    fetchTemplates();
  }, [fetchTimeBlocks, fetchWeekSchedule, fetchTemplates]);

  const handleCreateBlock = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        date: formData.recurrence === 'none' ? formData.date : null
      };
      
      if (editingBlock) {
        await api.put(`/time-blocks/${editingBlock.id}`, payload);
        toast.success('Time block updated!');
      } else {
        await api.post('/time-blocks', payload);
        toast.success('Time block created!');
      }
      
      setShowCreateModal(false);
      resetForm();
      fetchTimeBlocks();
      fetchWeekSchedule();
    } catch (error) {
      toast.error('Failed to save time block');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    if (!window.confirm('Delete this time block?')) return;
    
    try {
      await api.delete(`/time-blocks/${blockId}`);
      toast.success('Time block deleted!');
      fetchTimeBlocks();
      fetchWeekSchedule();
    } catch (error) {
      toast.error('Failed to delete time block');
    }
  };

  const handleApplyTemplate = async (templateName) => {
    try {
      await api.post(`/time-blocks/apply-template?template_name=${templateName}&recurrence=weekdays`);
      toast.success('Template applied successfully!');
      setShowTemplateModal(false);
      fetchTimeBlocks();
      fetchWeekSchedule();
    } catch (error) {
      toast.error('Failed to apply template');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      activity_type: 'Prospecting',
      start_time: '09:00',
      end_time: '10:00',
      date: '',
      recurrence: 'none',
      days_of_week: [],
      color: '#E31837',
      notes: '',
      reminder_minutes: 5
    });
    setEditingBlock(null);
  };

  const openEditModal = (block) => {
    setEditingBlock(block);
    setFormData({
      title: block.title,
      activity_type: block.activity_type,
      start_time: block.start_time,
      end_time: block.end_time,
      date: block.date || '',
      recurrence: block.recurrence,
      days_of_week: block.days_of_week || [],
      color: block.color,
      notes: block.notes || '',
      reminder_minutes: block.reminder_minutes || 5
    });
    setShowCreateModal(true);
  };

  const navigateWeek = (direction) => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + (direction * 7));
    setWeekStart(newStart);
  };

  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const getBlockPosition = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    
    const startOffset = (startHour - 6) * 60 + startMin;
    const duration = (endHour - startHour) * 60 + (endMin - startMin);
    
    return {
      top: `${(startOffset / 60) * 48}px`,
      height: `${(duration / 60) * 48}px`
    };
  };

  const isCurrentHour = (hour) => {
    return currentTime.getHours() === hour;
  };

  const getCurrentTimePosition = () => {
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    if (hours < 6 || hours > 22) return null;
    const offset = (hours - 6) * 60 + minutes;
    return `${(offset / 60) * 48}px`;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const getActivityIcon = (activityType) => {
    const activity = activityTypes.find(a => a.value === activityType);
    return activity ? activity.icon : Calendar;
  };

  const weekDates = getWeekDates();
  const currentTimePos = getCurrentTimePosition();

  return (
    <div className="space-y-6" data-testid="time-blocking-page">
      {/* Header with Live Clock */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#333333]">Time Blocking</h1>
          <p className="text-gray-600">Schedule and manage your daily activities</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Live Clock */}
          <Card className="p-4 bg-gradient-to-r from-[#E31837] to-[#B91430]">
            <div className="flex items-center gap-3">
              <Clock className="h-8 w-8 text-white" />
              <div className="text-white">
                <p className="text-2xl font-bold font-mono">{formatTime(currentTime)}</p>
                <p className="text-sm opacity-80">{formatDate(currentTime)}</p>
              </div>
            </div>
          </Card>
          
          {/* Notification Toggle */}
          <Button
            onClick={toggleNotifications}
            variant={notificationsEnabled ? "default" : "outline"}
            className={`flex items-center gap-2 ${notificationsEnabled ? 'bg-green-600 hover:bg-green-700' : ''}`}
            data-testid="notification-toggle"
            title={notificationsEnabled ? 'Notifications enabled' : 'Enable notifications'}
          >
            {notificationsEnabled ? (
              <>
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline">Alerts On</span>
              </>
            ) : (
              <>
                <BellOff className="h-4 w-4" />
                <span className="hidden sm:inline">Alerts Off</span>
              </>
            )}
          </Button>
          
          <Button
            onClick={() => setShowTemplateModal(true)}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="templates-button"
          >
            <Zap className="h-4 w-4" />
            Templates
          </Button>
          
          <Button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="bg-[#E31837] hover:bg-[#C4122C] flex items-center gap-2"
            data-testid="create-block-button"
          >
            <Plus className="h-4 w-4" />
            Add Time Block
          </Button>
        </div>
      </div>

      {/* Notification Status Banner */}
      {notificationPermission === 'denied' && (
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-3">
            <BellOff className="h-5 w-5 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800">Notifications Blocked</p>
              <p className="text-sm text-yellow-700">
                Please enable notifications in your browser settings to receive reminders for your scheduled activities.
              </p>
            </div>
          </div>
        </Card>
      )}

      {notificationsEnabled && (
        <Card className="p-3 bg-green-50 border-green-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-green-600" />
              <span className="text-sm text-green-800">
                <strong>Reminders Active</strong> - You will receive browser notifications before your scheduled time blocks
              </span>
            </div>
            <Badge className="bg-green-600 text-white">
              {timeBlocks.filter(b => b.reminder_minutes > 0).length} blocks with reminders
            </Badge>
          </div>
        </Card>
      )}

      {/* View Toggle and Navigation */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <Tabs value={activeView} onValueChange={setActiveView}>
            <TabsList>
              <TabsTrigger value="week">Week View</TabsTrigger>
              <TabsTrigger value="day">Day View</TabsTrigger>
              <TabsTrigger value="list">List View</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-medium px-4">
              {weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {
                new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
              }
            </span>
            <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setWeekStart(getWeekStart(new Date()))}>
              Today
            </Button>
          </div>
        </div>
      </Card>

      {/* Week View */}
      {activeView === 'week' && (
        <Card className="p-4 overflow-x-auto">
          <div className="min-w-[900px]">
            {/* Header Row */}
            <div className="grid grid-cols-8 gap-1 mb-2">
              <div className="w-16"></div>
              {weekDates.map((date, idx) => {
                const isToday = date.toDateString() === new Date().toDateString();
                const dateKey = date.toISOString().split('T')[0];
                return (
                  <div 
                    key={dateKey} 
                    className={`text-center p-2 rounded ${isToday ? 'bg-[#E31837] text-white' : 'bg-gray-100'}`}
                  >
                    <p className="font-medium text-sm">{daysOfWeek[idx].label}</p>
                    <p className={`text-xs ${isToday ? 'text-white/80' : 'text-gray-500'}`}>
                      {date.getDate()}
                    </p>
                  </div>
                );
              })}
            </div>
            
            {/* Time Grid */}
            <div className="relative">
              {/* Current Time Indicator */}
              {currentTimePos && (
                <div 
                  className="absolute left-16 right-0 h-0.5 bg-red-500 z-20 pointer-events-none"
                  style={{ top: currentTimePos }}
                >
                  <div className="absolute -left-2 -top-1.5 w-3 h-3 bg-red-500 rounded-full"></div>
                </div>
              )}
              
              {/* Hour Rows */}
              {hours.map(hour => (
                <div key={hour} className="grid grid-cols-8 gap-1" style={{ height: '48px' }}>
                  <div className={`w-16 text-xs text-right pr-2 pt-1 ${isCurrentHour(hour) ? 'text-[#E31837] font-bold' : 'text-gray-500'}`}>
                    {hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`}
                  </div>
                  {weekDates.map((date, dayIdx) => {
                    const dateStr = date.toISOString().split('T')[0];
                    return (
                      <div 
                        key={dayIdx} 
                        className="border-t border-gray-100 relative"
                        data-testid={`time-slot-${dateStr}-${hour}`}
                      ></div>
                    );
                  })}
                </div>
              ))}
              
              {/* Time Blocks Overlay */}
              <div className="absolute inset-0 grid grid-cols-8 gap-1 pointer-events-none" style={{ top: 0, left: 0 }}>
                <div className="w-16"></div>
                {weekDates.map((date, dayIdx) => {
                  const dateStr = date.toISOString().split('T')[0];
                  const dayBlocks = weekSchedule[dateStr] || [];
                  
                  return (
                    <div key={dayIdx} className="relative">
                      {dayBlocks.map((block, blockIdx) => {
                        const pos = getBlockPosition(block.start_time, block.end_time);
                        const Icon = getActivityIcon(block.activity_type);
                        
                        return (
                          <div
                            key={blockIdx}
                            className="absolute left-0 right-0 mx-0.5 rounded overflow-hidden shadow-sm cursor-pointer pointer-events-auto hover:opacity-90 transition-opacity"
                            style={{
                              ...pos,
                              backgroundColor: block.color || '#E31837'
                            }}
                            onClick={() => openEditModal(block)}
                            data-testid={`time-block-${block.id}`}
                          >
                            <div className="p-1 h-full text-white">
                              <div className="flex items-center gap-1">
                                <Icon className="h-3 w-3 flex-shrink-0" />
                                <p className="text-xs font-medium truncate">{block.title}</p>
                              </div>
                              <p className="text-[10px] opacity-80">{block.start_time} - {block.end_time}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Day View */}
      {activeView === 'day' && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">
              {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h2>
            <Input
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="w-40"
            />
          </div>
          
          <div className="relative">
            {/* Current Time Indicator */}
            {currentTimePos && selectedDate.toDateString() === new Date().toDateString() && (
              <div 
                className="absolute left-16 right-0 h-0.5 bg-red-500 z-20"
                style={{ top: currentTimePos }}
              >
                <div className="absolute -left-2 -top-1.5 w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="absolute -left-16 -top-2 text-xs text-red-500 font-medium">
                  {formatTime(currentTime).slice(0, 5)}
                </span>
              </div>
            )}
            
            {hours.map(hour => {
              const dateStr = selectedDate.toISOString().split('T')[0];
              const dayBlocks = (weekSchedule[dateStr] || []).filter(b => {
                const blockHour = parseInt(b.start_time.split(':')[0]);
                return blockHour === hour;
              });
              
              return (
                <div key={hour} className="flex border-t border-gray-100" style={{ minHeight: '60px' }}>
                  <div className={`w-20 text-sm text-right pr-4 pt-2 ${isCurrentHour(hour) ? 'text-[#E31837] font-bold' : 'text-gray-500'}`}>
                    {hour === 12 ? '12:00 PM' : hour > 12 ? `${hour - 12}:00 PM` : `${hour}:00 AM`}
                  </div>
                  <div className="flex-1 relative min-h-[60px] p-1">
                    {dayBlocks.map((block) => {
                      const Icon = getActivityIcon(block.activity_type);
                      return (
                        <div
                          key={block.id}
                          className="p-3 rounded-lg mb-1 cursor-pointer hover:opacity-90"
                          style={{ backgroundColor: block.color || '#E31837' }}
                          onClick={() => openEditModal(block)}
                        >
                          <div className="flex items-center justify-between text-white">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              <span className="font-medium">{block.title}</span>
                            </div>
                            <span className="text-sm">{block.start_time} - {block.end_time}</span>
                          </div>
                          {block.notes && (
                            <p className="text-xs text-white/80 mt-1">{block.notes}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* List View */}
      {activeView === 'list' && (
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">All Time Blocks</h2>
          
          {timeBlocks.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No time blocks created yet</p>
          ) : (
            <div className="space-y-3">
              {timeBlocks.map(block => {
                const Icon = getActivityIcon(block.activity_type);
                return (
                  <div
                    key={block.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: block.color }}
                      >
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-medium">{block.title}</h3>
                        <p className="text-sm text-gray-500">
                          {block.start_time} - {block.end_time} • {block.activity_type}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {block.recurrence === 'none' ? `One-time: ${block.date}` : 
                             block.recurrence === 'daily' ? 'Daily' :
                             block.recurrence === 'weekdays' ? 'Weekdays' :
                             `Weekly: ${block.days_of_week?.map(d => daysOfWeek[d].label).join(', ')}`}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(block)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteBlock(block.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Activity Legend */}
      <Card className="p-4">
        <h3 className="font-semibold mb-3">Activity Types</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {activityTypes.map(activity => {
            const Icon = activity.icon;
            return (
              <div key={activity.value} className="flex items-center gap-2 p-2 rounded" style={{ backgroundColor: `${activity.color}20` }}>
                <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: activity.color }}>
                  <Icon className="h-3 w-3 text-white" />
                </div>
                <span className="text-xs font-medium">{activity.label}</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="create-block-modal">
          <DialogHeader>
            <DialogTitle>{editingBlock ? 'Edit Time Block' : 'Create Time Block'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateBlock} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="e.g., Morning Prospecting"
                data-testid="block-title-input"
              />
            </div>

            <div>
              <Label>Activity Type</Label>
              <Select
                value={formData.activity_type}
                onValueChange={(v) => {
                  const activity = activityTypes.find(a => a.value === v);
                  setFormData({ 
                    ...formData, 
                    activity_type: v,
                    color: activity?.color || '#E31837'
                  });
                }}
              >
                <SelectTrigger data-testid="activity-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {activityTypes.map(activity => (
                    <SelectItem key={activity.value} value={activity.value}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: activity.color }}></div>
                        {activity.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="start_time">Start Time *</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                  data-testid="start-time-input"
                />
              </div>
              <div>
                <Label htmlFor="end_time">End Time *</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                  data-testid="end-time-input"
                />
              </div>
            </div>

            <div>
              <Label>Recurrence</Label>
              <Select
                value={formData.recurrence}
                onValueChange={(v) => setFormData({ ...formData, recurrence: v })}
              >
                <SelectTrigger data-testid="recurrence-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {recurrenceOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.recurrence === 'none' && (
              <div>
                <Label htmlFor="date">Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="block-date-input"
                />
              </div>
            )}

            {formData.recurrence === 'weekly' && (
              <div>
                <Label>Days of Week</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {daysOfWeek.map(day => (
                    <label key={day.value} className="flex items-center gap-1 cursor-pointer">
                      <Checkbox
                        checked={formData.days_of_week.includes(day.value)}
                        onCheckedChange={(checked) => {
                          const newDays = checked
                            ? [...formData.days_of_week, day.value]
                            : formData.days_of_week.filter(d => d !== day.value);
                          setFormData({ ...formData, days_of_week: newDays });
                        }}
                      />
                      <span className="text-sm">{day.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="color">Color</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="color"
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-16 h-10 p-1"
                />
                <span className="text-sm text-gray-500">{formData.color}</span>
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                placeholder="Optional notes..."
              />
            </div>

            <div>
              <Label htmlFor="reminder">Reminder (minutes before)</Label>
              <Select
                value={String(formData.reminder_minutes)}
                onValueChange={(v) => setFormData({ ...formData, reminder_minutes: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">No reminder</SelectItem>
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-[#E31837] hover:bg-[#C4122C]"
                data-testid="save-block-button"
              >
                {editingBlock ? 'Update' : 'Create'} Block
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Templates Modal */}
      <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
        <DialogContent className="max-w-2xl" data-testid="templates-modal">
          <DialogHeader>
            <DialogTitle>Schedule Templates</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <p className="text-sm text-gray-600">
              Apply a pre-built schedule template to quickly set up your time blocks. Templates are applied for weekdays.
            </p>
            
            {Object.entries(templates).map(([key, template]) => (
              <Card key={key} className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">{template.name}</h3>
                    <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {template.blocks?.map((block) => (
                        <Badge 
                          key={`${block.start_time}-${block.title}`} 
                          style={{ backgroundColor: block.color, color: 'white' }}
                          className="text-xs"
                        >
                          {block.start_time} {block.title}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <Button
                    onClick={() => handleApplyTemplate(key)}
                    className="bg-[#E31837] hover:bg-[#C4122C]"
                    data-testid={`apply-template-${key}`}
                  >
                    Apply
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
